import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'react-native-toast-notifications';

import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import {
  Team,
  TeamMemberRole,
  Project,
  useCreateChatMutation,
  useNotifyTeamOwnerMutation,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { MIXPANEL_EVENTS, TEAM_PLAN_FEATURES } from '@src/constants/constants';
import { useMixpanel } from '@utils/mixpanel';
import {
  hasReachedPersonalProjectLimit,
  isUserTrialEligible,
} from '@utils/teams/paywall';

interface TeamPaywallModalProps {
  visible: boolean;
  onClose: () => void;
  team?: Team;
  project?: Project;
}

export const TeamPaywallModal: React.FC<TeamPaywallModalProps> = ({
  visible,
  onClose,
  team,
  project,
}) => {
  const {
    setUpgradeConfirmModalOpen,
    setTeamValues,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setPaywallTeam,
    setShowPaywallTeamSetup,
    chatExpanded,
    chatListOpen,
    setChatListOpen,
    setShouldChatListOpen,
  } = useWebDrawer();
  const { setActiveMessageId, setIsOpenChat } = useActiveChat();
  const { t } = useTranslation('models');
  const toast = useToast();
  const { trackEvent } = useMixpanel();

  // Determine trial eligibility and role (needed for analytics before mutation is declared)
  const teamData = project?.team ?? team;
  const { teams: allTeams = [] } = useListTeamsFromQuery({
    includePending: true,
  });
  // TA-9872: subscriptionDetails was split out of listTeams — merge it back
  // from the getTeamBilling query for the trial-eligibility checks.
  const { billingByTeamId } = useTeamBillingFromQuery({
    includePending: true,
  });
  const allTeamsWithBilling = allTeams.map((item) => ({
    ...item,
    subscriptionDetails: billingByTeamId[item.id]?.subscriptionDetails,
  }));
  // Merge billing into the prop team too, so the fallback path (team not found
  // in allTeamsWithBilling) still carries subscriptionDetails — matching the
  // native modal's teamDataWithBilling and keeping isUserTrialEligible consistent.
  const teamDataWithBilling = teamData && {
    ...teamData,
    subscriptionDetails:
      teamData.subscriptionDetails ??
      billingByTeamId[teamData.id]?.subscriptionDetails,
  };
  const resolvedTeamData =
    teamData?.id != null
      ? allTeamsWithBilling.find(
          (item) => String(item.id) === String(teamData.id)
        ) ?? teamDataWithBilling
      : teamDataWithBilling;
  // Billing loads asynchronously (TA-9872). isUserTrialEligible infers
  // subscription history from subscriptionDetails across teams, so while
  // billing is empty every team looks history-free and a previously-subscribed
  // user would be wrongly marked eligible. Treat "not loaded yet" as ineligible
  // (re-evaluates once billing arrives).
  const billingReady =
    allTeams.length === 0 || Object.keys(billingByTeamId).length > 0;
  const isEligibleForTrial =
    billingReady && isUserTrialEligible(allTeamsWithBilling, resolvedTeamData);
  const isMember = resolvedTeamData?.currentUserRole === TeamMemberRole.Member;
  const isPersonalAtLimit = hasReachedPersonalProjectLimit(resolvedTeamData);
  const showProjectLimitContent = isPersonalAtLimit || isEligibleForTrial;

  const getModalVariant = () => {
    if (isEligibleForTrial) return 'trial_eligible';
    if (isPersonalAtLimit) return 'project_limit';
    return 'subscription_expired';
  };

  const buildPayload = () => ({
    modal_variant: getModalVariant(),
    user_role: resolvedTeamData?.currentUserRole?.toLowerCase() ?? 'unknown',
    team_type: resolvedTeamData?.personal ? 'personal' : 'team',
    team_id: resolvedTeamData?.id,
    platform: 'web',
  });

  useEffect(() => {
    if (visible) {
      trackEvent(MIXPANEL_EVENTS.PAYWALL_MODAL_SHOWN, buildPayload());
    }
  }, [visible]);

  const handleDismissClose = () => {
    trackEvent(MIXPANEL_EVENTS.PAYWALL_DISMISSED, buildPayload());
    onClose();
  };

  const { navigateToChatDrawer } = useAppNavigation();

  const [createChat, { data: createChatData }] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    if (!chatExpanded && chatListOpen) {
      setShouldChatListOpen(true);
      setChatListOpen(false);
    }
    setIsOpenChat(true);
    setActiveMessageId?.(undefined);
    navigateToChatDrawer({
      screen: ChatDrawerScreen.details,
      activeChat: createChatData.createChat,
    });
  }, [createChatData]);

  const [
    notifyTeamOwner,
    {
      loading: notifyLoading,
      data: notifyTeamOwnerData,
      error: notifyTeamOwnerError,
    },
  ] = useNotifyTeamOwnerMutation();

  useEffect(() => {
    if (!notifyTeamOwnerData) return;
    onClose();
    const ownerId = resolvedTeamData?.owner?.id;
    if (ownerId) {
      // TA-9872: no name passed — for 1:1 chats the backend derives the
      // personalized name from the other participant (listTeams no longer
      // carries owner.name).
      createChat({
        variables: {
          attributes: {
            userIds: [ownerId],
          },
        },
      });
    }
  }, [notifyTeamOwnerData]);

  useEffect(() => {
    if (!notifyTeamOwnerError) return;
    const code = notifyTeamOwnerError.graphQLErrors?.[0]?.extensions?.code as
      | string
      | undefined;
    let message = t('models:subscription.notifyOwner.failed');
    if (code === 'RATE_LIMITED') {
      message = t('models:subscription.notifyOwner.rateLimited');
    } else if (code === 'NO_RECIPIENTS') {
      message = t('models:subscription.notifyOwner.noRecipients');
    }
    toast.show(message, { type: 'danger', duration: 4000 });
  }, [notifyTeamOwnerError]);

  // Determine modal content based on project type and trial eligibility
  const title = showProjectLimitContent
    ? t('models:subscription.teamPaywallModal.titleTrialEligible')
    : t('models:subscription.teamPaywallModal.titleExpired');
  const description = showProjectLimitContent
    ? t('models:subscription.teamPaywallModal.descriptionTrialEligible')
    : t('models:subscription.teamPaywallModal.descriptionExpired');
  const featuresLabel = showProjectLimitContent
    ? t('models:subscription.teamPaywallModal.featuresLabelTrialEligible')
    : t('models:subscription.teamPaywallModal.featuresLabelExpired');

  // Determine button label and action
  const getButtonLabel = () => {
    if (showProjectLimitContent) {
      return t('models:subscription.teamPaywallModal.startFreeTrial');
    }
    if (isMember) return t('models:subscription.notifyOwner.action');
    return t('models:subscription.teamPaywallModal.upgradeToPlan');
  };

  const handleOpenUpgradeModal = () => {
    onClose();
    if (resolvedTeamData) {
      setTeamValues(resolvedTeamData);
      setIdTeamOpen(resolvedTeamData.id ?? '');
      setUUIdTeamOpen(resolvedTeamData.uuid ?? '');
    }
    setUpgradeConfirmModalOpen(true);
  };

  const getCtaAction = () => {
    if (isEligibleForTrial) return 'start_trial';
    if (isPersonalAtLimit) return 'create_team';
    if (isMember) return 'notify_owner';
    return 'upgrade';
  };

  const handleButtonPress = () => {
    trackEvent(MIXPANEL_EVENTS.PAYWALL_CTA_CLICKED, {
      ...buildPayload(),
      cta_action: getCtaAction(),
    });
    if (showProjectLimitContent) {
      // Personal teams at project limit always go to Create New Team flow
      onClose();
      if (resolvedTeamData) {
        setPaywallTeam(resolvedTeamData);
      }
      setShowPaywallTeamSetup(true);
    } else if (isMember) {
      const teamId = teamData?.id;
      if (teamId) {
        notifyTeamOwner({ variables: { teamId } });
      }
    } else {
      handleOpenUpgradeModal();
    }
  };

  return (
    <WebModal
      accessibilityLabel={t(
        'models:subscription.teamPaywallModal.accessibilityLabel'
      )}
      visible={visible}
      onClose={handleDismissClose}
      width={650}
      height={undefined}>
      <Box
        flex={1}
        flexDirection='column'
        alignItems='center'
        paddingHorizontal='l'
        paddingVertical='xl'>
        {/* Crown Icon */}
        <Box
          width={72}
          height={72}
          borderRadius='m'
          backgroundColor='greenSecondaryMild'
          alignItems='center'
          justifyContent='center'
          marginBottom='m'>
          <Icon
            name='CrownOutline'
            variant='s'
            color='greenSecondary'
            width={36}
            height={36}
          />
        </Box>

        {/* Title */}
        <Text
          variant='heading2'
          color='textPrimary'
          textAlign='center'
          marginBottom='xs'>
          {title}
        </Text>

        {/* Description */}
        <Text
          variant='body'
          color='textPrimary'
          textAlign='center'
          marginBottom='l'
          style={{ maxWidth: 500 }}>
          {description}
        </Text>

        {/* Features Section */}
        <Box
          width='100%'
          backgroundColor='grey01'
          borderRadius='m'
          padding='m'
          marginBottom='l'>
          <Text variant='webBody' color='textPrimary' marginBottom='s'>
            {featuresLabel}
          </Text>
          {TEAM_PLAN_FEATURES.map((feature: string, index: number) => (
            <Box
              key={feature}
              flexDirection='row'
              alignItems='center'
              marginTop={index > 0 ? 'xs' : 'none'}>
              <Icon
                name='Check'
                variant='s'
                color='greenSecondary'
                marginRight='xs'
              />
              <Text
                variant='metadata'
                color='textPrimary'
                style={{ letterSpacing: 0.24 }}>
                {feature}
              </Text>
            </Box>
          ))}
        </Box>

        {/* Button */}
        <Button
          fullWidth
          variant='primary'
          backgroundColor='textSecondary'
          borderWidth={0}
          onPress={handleButtonPress}
          disabled={notifyLoading}
          height={48}
          borderRadius='s'
          accessibilityLabel={getButtonLabel()}>
          <Text variant='buttonLabel' color='white'>
            {notifyLoading
              ? t('models:subscription.teamPaywallModal.notifying')
              : getButtonLabel()}
          </Text>
        </Button>
      </Box>
    </WebModal>
  );
};
